"use client";

import { useState } from "react";
import { useSearchParams } from "next/navigation";
import { formatPrice } from "@/lib/utils";
import { LocalDateTime } from "@/components/ui/LocalDateTime";
import type { OrderStatus, PaymentMethod, PaymentStatus } from "@/types";

const STATUS_MAP: Record<OrderStatus, string> = {
  PENDING: "주문 접수",
  CONFIRMED: "주문 확인",
  PREPARING: "배송 준비 중",
  SHIPPING: "배송 중",
  DELIVERED: "배송 완료",
  CANCELLED: "취소",
};

const PAYMENT_STATUS_MAP: Record<PaymentStatus, string> = {
  UNPAID: "미결제",
  PAID: "결제 완료",
  CANCELLED: "취소",
  REFUNDED: "환불",
  PARTIAL_REFUNDED: "부분환불",
};

const PAYMENT_METHOD_MAP: Record<PaymentMethod, string> = {
  CARD: "카드/간편결제",
  NICEPAY: "카드/간편결제",
  BANK_TRANSFER: "무통장입금",
  PHONE: "전화주문",
  KAKAOPAY: "카카오페이",
  TOSSPAY: "토스페이",
  NAVERPAY: "Npay",
};

interface OrderItem {
  name: string;
  quantity: number;
  price: number;
  unit: string | null;
}

interface OrderData {
  orderNumber: string;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  totalAmount: number;
  deliveryFee: number;
  isPreorder: boolean;
  customerName: string;
  recipientName: string;
  address: string;
  addressDetail: string | null;
  deliveryMemo: string | null;
  trackingNumber: string | null;
  createdAt: string;
  cancelReason: string | null;
  cancelledAt: string | null;
  items: OrderItem[];
}

export default function OrderLookupPage() {
  const searchParams = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(
    searchParams.get("orderNumber") ?? "",
  );
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState<OrderData | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelError, setCancelError] = useState("");

  async function handleCancel() {
    if (!order) return;
    setCancelling(true);
    setCancelError("");
    try {
      const res = await fetch("/api/orders/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderNumber: order.orderNumber, phone }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error ?? "취소 실패");
      setOrder((prev) =>
        prev
          ? { ...prev, status: "CANCELLED", paymentStatus: "CANCELLED" }
          : prev,
      );
      setConfirmCancel(false);
    } catch (err) {
      setCancelError(
        err instanceof Error ? err.message : "취소 중 오류가 발생했습니다.",
      );
    } finally {
      setCancelling(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);

    try {
      // POST, not GET: a GET query string ends up in server/CDN access
      // logs and (if performance capture is remote-enabled) PostHog's
      // network-timing entries, and phone is not something to leave sitting
      // in either.
      const res = await fetch("/api/orders/lookup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderNumber, phone }),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error ?? "오류가 발생했습니다.");
      } else {
        setOrder(data.data);
      }
    } catch {
      setError("네트워크 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="container section-padding">
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="section-title">
          <h1 className="text-3xl">주문 조회</h1>
          <span className="gold-line" />
        </div>

        <div className="card p-6 space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label" htmlFor="orderNumber">
                주문번호 <span className="text-[var(--color-error)]">*</span>
              </label>
              <input
                id="orderNumber"
                className="input"
                placeholder="HF20250101xxxxxxxx"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="phone">
                휴대폰 번호 <span className="text-[var(--color-error)]">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                className="input"
                placeholder="010-1234-5678"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            {error && (
              <p className="text-sm text-[var(--color-error)]">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? "조회 중..." : "조회하기"}
            </button>
          </form>
        </div>

        {order && (
          <div className="card p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-[var(--color-primary)]">
                주문 정보
              </h2>
              <span className="text-sm font-semibold px-3 py-1 rounded-full bg-[var(--color-cream)] text-[var(--color-primary)]">
                {STATUS_MAP[order.status]}
              </span>
            </div>

            {order.status === "CANCELLED" && (
              <div
                className="rounded-lg border-2 p-3 space-y-1"
                style={{
                  borderColor: "var(--color-error)",
                  backgroundColor: "rgba(220, 53, 69, 0.05)",
                }}
              >
                <p className="text-sm font-semibold text-[var(--color-error)]">
                  주문이 취소되었습니다
                </p>
                {order.cancelReason && (
                  <p className="text-sm text-[var(--color-text-dark)] whitespace-pre-line ph-mask ph-no-capture">
                    사유: {order.cancelReason}
                  </p>
                )}
                {(order.paymentStatus === "REFUNDED" ||
                  order.paymentStatus === "CANCELLED") && (
                  <p className="text-xs text-[var(--color-text-light)]">
                    {order.paymentMethod === "BANK_TRANSFER"
                      ? "무통장입금 건입니다. 입금 확인 전이라면 추가 처리는 없습니다. 이미 입금하신 경우에는 안내드린 계좌로 환불 처리되며, 빠른 진행을 위해 고객센터로 환불 받을 계좌 정보를 알려주시면 감사하겠습니다."
                      : "결제 금액은 결제 수단으로 자동 환불됩니다. 카드사에 따라 영업일 기준 3~5일 소요될 수 있습니다."}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--color-text-light)]">주문번호</span>
                <span className="font-bold font-mono ph-mask ph-no-capture">
                  {order.orderNumber}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-text-light)]">주문일</span>
                <span>
                  <LocalDateTime iso={order.createdAt} variant="date" />
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-text-light)]">
                  결제 수단
                </span>
                <span>{PAYMENT_METHOD_MAP[order.paymentMethod]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-text-light)]">
                  결제 상태
                </span>
                <span
                  className={
                    order.paymentStatus === "PAID"
                      ? "text-[var(--color-success)] font-bold"
                      : ""
                  }
                >
                  {PAYMENT_STATUS_MAP[order.paymentStatus]}
                </span>
              </div>
            </div>

            <div className="pt-3 border-t border-[var(--color-border)] space-y-2">
              <p className="font-semibold text-[var(--color-text-medium)] text-sm">
                주문 상품
              </p>
              {order.items.map((item, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-[var(--color-text-dark)]">
                    {item.name} x {item.quantity}
                    {item.unit ? ` (${item.unit})` : ""}
                  </span>
                  <span>{formatPrice(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>

            <div className="pt-3 border-t border-[var(--color-border)] space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--color-text-light)]">
                  상품 금액
                </span>
                <span>
                  {formatPrice(order.totalAmount - order.deliveryFee)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--color-text-light)]">배송비</span>
                <span>
                  {order.deliveryFee === 0 ? (
                    <span className="text-[var(--color-success)]">무료</span>
                  ) : (
                    formatPrice(order.deliveryFee)
                  )}
                </span>
              </div>
              <div className="flex justify-between font-bold text-base pt-1">
                <span className="text-[var(--color-primary)]">총 결제금액</span>
                <span className="text-[var(--color-primary)]">
                  {formatPrice(order.totalAmount)}
                </span>
              </div>
            </div>

            <div className="pt-3 border-t border-[var(--color-border)] space-y-2 text-sm">
              <p className="font-semibold text-[var(--color-text-medium)]">
                배송지
              </p>
              <p className="text-[var(--color-text-dark)] ph-mask ph-no-capture">
                {order.address}
                {order.addressDetail ? ` ${order.addressDetail}` : ""}
              </p>
              <p className="text-[var(--color-text-dark)] ph-mask ph-no-capture">
                {order.recipientName}
              </p>
              {order.deliveryMemo && (
                <p className="text-[var(--color-text-light)]">
                  배송메모:&nbsp;
                  <span className="ph-mask ph-no-capture">
                    {order.deliveryMemo}
                  </span>
                </p>
              )}
            </div>

            {order.trackingNumber && (
              <div className="pt-3 border-t border-[var(--color-border)]">
                <div className="p-4 rounded-xl bg-[var(--color-cream)] space-y-1 text-sm">
                  <p className="font-semibold text-[var(--color-text-medium)] mb-2">
                    배송 추적
                  </p>
                  <p className="font-mono font-bold text-lg text-[var(--color-primary)] ph-mask ph-no-capture">
                    {order.trackingNumber}
                  </p>
                  <p className="text-[var(--color-text-light)] text-xs">
                    우체국택배 사이트에서 운송장 번호로 조회하세요.
                  </p>
                </div>
              </div>
            )}

            {/* 취소/문의 안내 */}
            {order.status === "PENDING" && (
              <div className="pt-3 border-t border-[var(--color-border)]">
                {confirmCancel ? (
                  <div
                    className="p-4 rounded-xl bg-[#FEF2F2] space-y-3 text-sm"
                    role="alert"
                    aria-live="polite"
                  >
                    <p className="font-semibold text-[var(--color-error)]">
                      정말 주문을 취소하시겠습니까?
                    </p>
                    <p className="text-[var(--color-text-medium)]">
                      취소 후에는 복구할 수 없습니다.
                    </p>
                    {cancelError && (
                      <p className="text-[var(--color-error)] text-xs">
                        {cancelError}
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleCancel}
                        disabled={cancelling}
                        className="flex-1 py-2 rounded-lg border-2 text-sm font-semibold transition-colors disabled:opacity-60"
                        style={{
                          borderColor: "var(--color-error)",
                          color: "var(--color-error)",
                        }}
                      >
                        {cancelling ? "취소 처리 중..." : "네, 취소합니다"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmCancel(false)}
                        disabled={cancelling}
                        className="flex-1 py-2 rounded-lg border-2 border-[var(--color-border)] text-sm font-semibold text-[var(--color-text-medium)] transition-colors hover:bg-[var(--color-cream)] disabled:opacity-60"
                      >
                        돌아가기
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(true)}
                    className="w-full py-2.5 rounded-lg border-2 text-sm font-semibold transition-colors hover:bg-[#FEF2F2]"
                    style={{
                      borderColor: "var(--color-error)",
                      color: "var(--color-error)",
                    }}
                  >
                    주문 취소
                  </button>
                )}
              </div>
            )}

            {(order.status === "CONFIRMED" || order.status === "PREPARING") && (
              <div className="pt-3 border-t border-[var(--color-border)]">
                <div className="p-4 rounded-xl bg-[var(--color-cream)] text-sm text-center space-y-3">
                  <p className="text-[var(--color-text-medium)]">
                    취소를 원하시면 고객센터로 연락해주세요.
                  </p>
                  <a
                    href="/contact"
                    className="btn-outline text-sm py-1.5 px-4"
                  >
                    고객센터 문의
                  </a>
                </div>
              </div>
            )}

            {(order.status === "SHIPPING" || order.status === "DELIVERED") && (
              <div className="pt-3 border-t border-[var(--color-border)]">
                <div className="p-4 rounded-xl bg-[var(--color-cream)] text-sm text-center space-y-3">
                  <p className="text-[var(--color-text-medium)]">
                    {order.status === "SHIPPING"
                      ? "배송이 시작된 주문입니다."
                      : "배송이 완료된 주문입니다."}{" "}
                    반품은 수령 후 고객센터로 문의해주세요.
                  </p>
                  <a
                    href="/contact"
                    className="btn-outline text-sm py-1.5 px-4"
                  >
                    고객센터 문의
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
